import { useRef } from 'react';
import { C, POLICE_CORPS } from '../../theme';

export interface DateEnTroisChamps {
  jour: string;
  mois: string;
  annee: string;
}

export const DATE_VIDE: DateEnTroisChamps = { jour: '', mois: '', annee: '' };

/**
 * Compose AAAA-MM-JJ depuis les trois champs.
 * Rend undefined si tout est vide (la date est facultative), null si elle est
 * incomplete — l'existence reelle de la date reste jugee par birthDateSchema.
 */
export function composerDate(champs: DateEnTroisChamps): string | undefined | null {
  const jour = champs.jour.trim();
  const mois = champs.mois.trim();
  const annee = champs.annee.trim();
  if (jour === '' && mois === '' && annee === '') return undefined;
  if (jour === '' || mois === '' || annee === '' || annee.length !== 4) return null;
  return `${annee}-${mois.padStart(2, '0')}-${jour.padStart(2, '0')}`;
}

/**
 * L'inverse de composerDate : pre-remplit les trois champs depuis AAAA-MM-JJ.
 * Tolere undefined : le champ vient du reseau, le compilateur ne garantit rien.
 */
export function decomposerDate(jour: string | null | undefined): DateEnTroisChamps {
  if (jour == null) return DATE_VIDE;
  const [annee = '', mois = '', jourDuMois = ''] = jour.split('-');
  return { jour: jourDuMois, mois, annee };
}

/**
 * Date de naissance en trois champs — Jour / Mois / Annee.
 *
 * Deuxieme version, apres deux echecs constates par le porteur sur son telephone :
 * le calendrier natif obligeait a defiler les mois jusqu'en 2010, et le champ texte
 * en clavier numerique n'avait pas de touche « / ». Trois champs numeriques separes
 * n'ont besoin ni de l'un ni de l'autre : c'est le motif de reference pour les dates
 * de naissance (celui des formulaires administratifs britanniques).
 */
export function ChampsDateNaissance({
  valeur,
  onChange,
}: {
  valeur: DateEnTroisChamps;
  onChange: (champs: DateEnTroisChamps) => void;
}) {
  const refMois = useRef<HTMLInputElement>(null);
  const refAnnee = useRef<HTMLInputElement>(null);

  const styleChamp = (largeur: number) => ({
    width: largeur,
    height: 48,
    border: `1px solid ${C.bordure}`,
    borderRadius: 12,
    padding: '0 0',
    textAlign: 'center' as const,
    fontSize: 16,
    fontFamily: POLICE_CORPS,
    color: C.encre,
    background: C.carte,
    outline: 'none',
  });

  const chiffresSeulement = (texte: string, maximum: number) =>
    texte.replace(/\D/g, '').slice(0, maximum);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: C.secondaire }}>
        Sa date de naissance (facultatif)
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            aria-label="Jour"
            inputMode="numeric"
            placeholder="JJ"
            value={valeur.jour}
            onChange={(e) => {
              const jour = chiffresSeulement(e.target.value, 2);
              onChange({ ...valeur, jour });
              // Deux chiffres saisis : on passe au champ suivant tout seul.
              if (jour.length === 2) refMois.current?.focus();
            }}
            style={styleChamp(64)}
          />
          <span style={{ fontSize: 11, color: C.tertiaire, textAlign: 'center' }}>Jour</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            ref={refMois}
            aria-label="Mois"
            inputMode="numeric"
            placeholder="MM"
            value={valeur.mois}
            onChange={(e) => {
              const mois = chiffresSeulement(e.target.value, 2);
              onChange({ ...valeur, mois });
              if (mois.length === 2) refAnnee.current?.focus();
            }}
            style={styleChamp(64)}
          />
          <span style={{ fontSize: 11, color: C.tertiaire, textAlign: 'center' }}>Mois</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            ref={refAnnee}
            aria-label="Année"
            inputMode="numeric"
            placeholder="AAAA"
            value={valeur.annee}
            onChange={(e) => onChange({ ...valeur, annee: chiffresSeulement(e.target.value, 4) })}
            style={styleChamp(88)}
          />
          <span style={{ fontSize: 11, color: C.tertiaire, textAlign: 'center' }}>Année</span>
        </div>
      </div>
      <span style={{ fontSize: 12.5, color: C.tertiaire, lineHeight: 1.45 }}>
        Par exemple 14 05 2010. Elle permet à Colette d'adapter ses propositions à l'âge.
      </span>
    </div>
  );
}
